"use client";

import React, { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { ROOM_PHOTOS, roomPhotoLayout, type RoomPhotoScene } from "./room-photos";

export interface CardRoomPreviewProps {
  src: string;
  /** The size it hangs at, so the sofa below measures it honestly. */
  widthMm: number;
  heightMm: number;
  /** Faded in while the card is hovered, out when it is left. */
  visible: boolean;
  /** Which measured room it hangs in. */
  scene?: RoomPhotoScene;
}

/**
 * The card's hover view: the work hanging above the sofa, to scale.
 *
 * The same photographs and the same arithmetic as the artwork page's room
 * preview (room-photos.ts), drawn into the card's frame instead of a wide
 * scene. Only mounted for a card someone has rested a mouse on, and only shown
 * once the room has loaded, so a slow connection never flashes an empty wall.
 */
export default function CardRoomPreview({
  src,
  widthMm,
  heightMm,
  visible,
  scene = "sofa",
}: CardRoomPreviewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [loaded, setLoaded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setBox({ width: el.clientWidth, height: el.clientHeight });
  }, []);

  const photo = ROOM_PHOTOS[scene];
  const fitted =
    box.width > 0
      ? roomPhotoLayout(photo, box.width, box.height, {
          widthCm: widthMm / 10,
          heightCm: heightMm / 10,
        })
      : null;
  // Only a room that fills the whole frame. Where the photograph runs out
  // before the frame does, the card keeps its plain picture instead of
  // showing a strip of empty colour beside the room.
  const layout = fitted && fitted.sceneWidth >= box.width - 1 ? fitted : null;

  const pxPerCm = layout?.pxPerCm ?? 0;
  const artWidth = (widthMm / 10) * pxPerCm;
  const artHeight = (heightMm / 10) * pxPerCm;
  const left = layout ? (box.width - layout.sceneWidth) / 2 : 0;
  const artTop = layout
    ? layout.floorY - photo.hangCentreCm * pxPerCm - artHeight / 2
    : 0;

  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        opacity: visible && loaded ? 1 : 0,
        transition: "opacity .3s ease",
        pointerEvents: "none",
      }}
    >
      {layout && (
        <Box sx={{ position: "absolute", left, top: 0, width: layout.sceneWidth, height: layout.sceneHeight }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={photo.src}
            alt=""
            onLoad={() => setLoaded(true)}
            style={{
              position: "absolute",
              left: layout.img.left,
              top: layout.img.top,
              width: layout.img.width,
              height: layout.img.height,
              maxWidth: "none",
            }}
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt=""
            style={{
              position: "absolute",
              left: (layout.sceneWidth - artWidth) / 2,
              top: artTop,
              width: artWidth,
              height: artHeight,
              objectFit: "cover",
              boxShadow: "0 10px 18px -8px rgba(20,24,32,.55)",
            }}
          />
        </Box>
      )}
    </Box>
  );
}
